/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useRef, useState} from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {Coordinates} from '@dnd-kit/utilities';
import {DownloadJobTasksElem} from '@ion/core/src/types/entities';
import {
  dependentTaskColors,
  getLastChildIndex,
  reorderTaskDependencies
} from '../helpers';
import SortableTask from './SortableTask';

export interface TasksDraggableListProps {
  jobTasks: DownloadJobTasksElem[];
  allParentTaskIds?: string[];
  setTasks: Function;
  renderContent: Function;
  handleDependentTask?: Function;
};

// Thresholds value to assign dependent tasks while dragging ...
enum DragDependencyThresholds {
  MIN_DISTANCE = 25,
  MAX_DISTANCE = 70,
  LEFT = 28
};

/**
 * Defines the TasksDraggableList component. This component displays a list of tasks and
 * supports drag-and-drop functionality for reordering them.
 *
 * ```typescript
 *
 *  import {TasksDraggableList} from './dependentTaskDnd/TasksDraggableList';
 * 
 *  <TasksDraggableList jobTasks={jobTasks}
 *    setTasks={setTasks}
 *    renderContent={renderTaskContent} />
 *   </TableBody>
 *
 * ```
 */
function TasksDraggableList({
  jobTasks,
  setTasks,
  renderContent,
  allParentTaskIds,
  handleDependentTask,
  ...props
}: TasksDraggableListProps) {
  const [activeId, setActiveId]               = useState<UniqueIdentifier | null>(null);
  const [activeIndex, setActiveIndex]         = useState<number | null>(null);
  const [colorIndex, setColorIndex]           = useState<number | null>(null);
  const [isOutsideParent, setIsOutsideParent] = useState(false);
  const [isBelowChild, setIsBelowChild]       = useState(false);
  const initialPositions                      = useRef({});
  const currentPositions                      = useRef({});
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter : sortableKeyboardCoordinates
    })
  );

  const setTrackOrder = useCallback((item: DownloadJobTasksElem, index: number) => {
    if (jobTasks) {
      const {children} = item?.task;
      if (children?.length) {
        const parentIndex = allParentTaskIds?.indexOf(item.id);
        jobTasks[index].trackLine = {
          number     : parentIndex,
          startIndex : index,
          endIndex   : getLastChildIndex(jobTasks, item),
          color      : dependentTaskColors(parentIndex)?.main
        };
      }
    }
  }, [allParentTaskIds, jobTasks]);

  function handleDragStart({active}: DragStartEvent) {
    const activeId                     = active.id;
    const activeElement                = document.querySelector(`[data-task-id='${activeId}']`);
    const activeRect                   = activeElement?.getBoundingClientRect();
    initialPositions.current[activeId] = activeRect.top;
    setActiveId(activeId);
    setIsOutsideParent(false);
    setIsBelowChild(false);
    setActiveIndex(null);
    setColorIndex(null);
  }

  const updateTaskDependencies = useCallback(({
    tasks,
    task,
    delta,
    taskRect,
    activeRect,
    activeIndex
  }) => {
    const distance = activeRect.top - taskRect.top;
    if (distance >= DragDependencyThresholds.MIN_DISTANCE &&
      distance <= DragDependencyThresholds.MAX_DISTANCE &&
      delta.x >= DragDependencyThresholds.LEFT &&
      activeRect.bottom > taskRect.top) {
      const overIndex        = tasks?.findIndex((item) => item.id === task.id);
      const parentIndex      = allParentTaskIds?.indexOf(`${task.id}`);
      const hasDependentTask = tasks[activeIndex]?.task?.metaData?.dependency?.index;
      setColorIndex(parentIndex);
      if (!hasDependentTask) {
        handleDependentTask({
          taskIndex    : activeIndex,
          tasks        : {tasks: tasks},
          selectedTask : overIndex + 1
        });
        setActiveIndex(activeIndex);
      }
    }
  }, [allParentTaskIds]);

  const handleDependencyUpdate = useCallback((
    tasks: DownloadJobTasksElem[],
    activeId: UniqueIdentifier,
    delta: Coordinates
  ) => {
    const activeElement                = document.querySelector(`[data-task-id='${activeId}']`);
    const activeRect                   = activeElement?.getBoundingClientRect();
    currentPositions.current[activeId] = activeRect.top;
    const initialPosition              = initialPositions.current[activeId];

    if (activeRect.top > initialPosition) {
      return;
    }
    const activeIndex = tasks?.findIndex((task) => task.id === activeId);
    tasks?.forEach((task) => {
      if (task.id !== activeId) {
        const taskElement = document.querySelector(`[data-task-id='${task.id}']`);
        if (taskElement) {
          const taskRect = taskElement.getBoundingClientRect();
          updateTaskDependencies({tasks, task, delta, taskRect, activeRect, activeIndex});
        }
      }
    });
  }, [jobTasks, allParentTaskIds]);

  function handleDragMove(event: DragMoveEvent) {
    const {active, over, delta} = event;
    const activeId              = active.id;
    handleDependencyUpdate(jobTasks, activeId, delta);

    if (!over || active.id === over.id) {
      setIsOutsideParent(false);
      return;
    };

    const {ancestorIds: activeAncestorIds = []} = active.data.current;
    const {ancestorIds: overAncestorIds = []}   = over.data.current;
    if (activeAncestorIds.includes(over.id) || overAncestorIds?.includes(active.id)) {
      setIsOutsideParent(true);
      setIsBelowChild(overAncestorIds?.includes(active.id));
    }
  }

  function handleDragEnd({active, over}: DragEndEvent) {
    const activeId = active.id;
    const overId   = over?.id;
    setActiveIndex(null);
    setColorIndex(null);

    if (!isOutsideParent && (over && activeId !== overId)) {
      const activeIndex = jobTasks.findIndex((task) => task.id === activeId);
      const overIndex   = jobTasks.findIndex((task) => task.id === overId);
      
      if (activeIndex > overIndex) {
        setIsOutsideParent(false);
        setIsBelowChild(false);
        setTasks((items) => {
          const {tasks} = items;
          if (tasks) {
            const updatedTasks = arrayMove(tasks, activeIndex, overIndex).map((task, index) => ({
              ...task,
              task : {
                ...task.task,
                name : `task_${index}`,
                index
              }
            }));
            return {
              tasks : reorderTaskDependencies(items?.tasks, updatedTasks)
            };
          }
        });
      }
    }
  };
  
  if (jobTasks) {
    return (
      <DndContext sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragMove={handleDragMove}
        onDragStart={handleDragStart} >
        <SortableContext items={jobTasks}
          strategy={verticalListSortingStrategy}>
          {jobTasks?.map((item, index) => {
            setTrackOrder(item, index);
            return (
              <SortableTask key={`job-tasks-${item.id}`}
                item={item}
                index={index}
                jobTasks={jobTasks}
                colorIndex={colorIndex}
                activeIndex={activeIndex}
                isBelowChild={isBelowChild}
                isOutsideParent={isOutsideParent}
                renderContent={renderContent} />
            );
          })}
        </SortableContext>
      </DndContext>
    );
  }
}

export default TasksDraggableList;
